import { defineStore } from 'pinia';
import { usePlannerStore } from '@/views/3d-planner/store/plannerStore';
import type { PlannerStore } from '@/views/3d-planner/store/plannerStore.types';
import { computed, type ComputedRef, type Ref, ref, type WritableComputedRef } from 'vue';
import { isDualMobilityVisible } from '@/util/versioning';
import { CocrOffset, HeadSystem } from '@/domain/catalog/head';
import { headSystemForStem } from '@/domain/catalog/stem';
import { getCatalogComponent } from '@/domain/catalog/catalog';
import { computeStem } from '@/views/3d-planner/fitting/componentUrls';
import {
    applyPartialRotationInRadiographicDegrees,
    getCupRotationInRadiographicDegrees,
} from '@/domain/anatomy/pelvis/acetabularAngles';
import type { AnteversionInclinationAxis } from '@/domain/anatomy/pelvis/anteversionInclination';
import { isDualMobilityDisabledByCupSize } from '@/domain/catalog/cup';
import { Vector3 } from 'three';
import { round } from '@/util/math';
import { templateCupPropertiesAreEqual } from '@/views/3d-planner/template/templateComparison';
import { syncTemplate } from '@/views/3d-planner/template/manualTemplateState';
import type { CupOverlayViewModel } from './cupOverlay.types';
import { selectableCups } from './cupSelection';

// Whether the cup reset is disabled: no auto-template, or the current cup matches it.
function isCupResetDisabled(planner: PlannerStore): boolean {
    if (!planner.case?.autoTemplate || !planner.template) {
        return true;
    }
    const current = syncTemplate(planner.template);
    return templateCupPropertiesAreEqual(current, planner.case.autoTemplate);
}

// Write a world-space cup offset back to the template, projected onto the cup basis.
function setCupWorldOffset(planner: PlannerStore, newWorldOffset: Vector3): void {
    if (!planner.template || !planner.fittedCup) {
        return;
    }
    planner.template.cupOffset = {
        ml: round(newWorldOffset.dot(planner.fittedCup.basis.lateral), 2),
        ap: round(newWorldOffset.dot(planner.fittedCup.basis.posterior), 2),
        si: round(newWorldOffset.dot(planner.fittedCup.basis.inferior), 2),
    };
}

function makeSelectedCupIndex(
    planner: PlannerStore,
    availableCups: ComputedRef<CupOverlayViewModel['availableCups']>,
): WritableComputedRef<number | undefined> {
    return computed<number | undefined>({
        get: () => {
            const template = planner.template;
            if (!template) {
                return undefined;
            }
            const index = availableCups.value.findIndex((cup) => cup.catalogUrl === template.cupUrl);
            return index >= 0 ? index : undefined;
        },
        set: (index: number | undefined) => {
            if (index !== undefined) {
                planner.setCup(availableCups.value[index].catalogUrl);
            }
        },
    });
}

function makeRotationComputed(
    planner: PlannerStore,
    axis: AnteversionInclinationAxis,
): WritableComputedRef<number | undefined> {
    return computed<number | undefined>({
        get: () =>
            planner.template
                ? getCupRotationInRadiographicDegrees(planner.template.cupRotation)[axis]
                : undefined,
        set: (value: number | undefined) => {
            if (value !== undefined && planner.template) {
                const angles =
                    axis === 'anteversion' ? { anteversion: value } : { inclination: value };
                planner.template.cupRotation = applyPartialRotationInRadiographicDegrees(
                    planner.template.cupRotation,
                    angles,
                );
            }
        },
    });
}

export const useCupOverlayViewModel: () => CupOverlayViewModel = defineStore('cup-overlay', () => {
    const planner = usePlannerStore();

    const isCoCrMinus6HeadSelected = computed(() => {
        const template = planner.template;
        const catalog = planner.catalog;
        if (!template || !catalog) {
            return false;
        }
        const stemSystem = computeStem(planner).system;
        const headOffset = getCatalogComponent(catalog.heads, template.headUrl).offset;
        return (
            headSystemForStem(stemSystem) === HeadSystem.COCR_1214 &&
            headOffset === CocrOffset.MINUS_6
        );
    });

    const selectedCupSize = computed<number | undefined>(
        () => planner.selectedCup?.outerDiameter ?? undefined,
    );

    const dualMobility = computed<boolean>({
        get: () => planner.dualMobility,
        set: planner.setDualMobility,
    });

    const availableCups = computed(() =>
        selectableCups([...(planner.fittedComponents?.cups.values() ?? [])], {
            dualMobility: dualMobility.value,
            restrictMinus6Head: isCoCrMinus6HeadSelected.value,
        }),
    );

    const coverage: Ref<number | null | 'calculating'> = ref(null);

    return {
        availableCups,
        selectedCupSize,
        selectedCupIndex: makeSelectedCupIndex(planner, availableCups),
        anteversion: makeRotationComputed(planner, 'anteversion'),
        inclination: makeRotationComputed(planner, 'inclination'),
        coverage,
        dualMobility,
        dualMobilityDisabled: computed(
            () =>
                isDualMobilityDisabledByCupSize(selectedCupSize.value) ||
                isCoCrMinus6HeadSelected.value,
        ),
        dualMobilityVisible: computed(() =>
            isDualMobilityVisible(planner.case?.caseVersion ?? null),
        ),
        isResetCupDisabled: computed(() => isCupResetDisabled(planner)),
        resetCup: () => planner.resetCup(),
        fittedCup: computed(() => planner.fittedCup),
        templateOffset: computed(() => planner.template?.cupOffset),
        cupAlignMode: computed(() => planner.case?.specification.alignmentMode),
        setWorldOffset: (offset: Vector3) => setCupWorldOffset(planner, offset),
    };
});